import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AIUsageDashboard from '@/components/AIUsageDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { generateDefaultPassword } from '@/lib/generateDefaultPassword';
import { 
  Shield, Building2, Users, GraduationCap, FileText, Activity,
  Plus, Edit, Trash2, Power, PowerOff, UserCheck, Eye,
  LogOut, BarChart3, School, TrendingUp, Loader2, UserPlus, Zap, ImageIcon, Volume2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [headTeachers, setHeadTeachers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [showEditSchool, setShowEditSchool] = useState(false);
  const [showSchoolDetails, setShowSchoolDetails] = useState(false);
  const [showAddHeadTeacher, setShowAddHeadTeacher] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [preGenProgress, setPreGenProgress] = useState(null);
  const [preGenPolling, setPreGenPolling] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  // "Add User to Existing School" dialog
  const [addUserDialog, setAddUserDialog] = useState({ open: false, school: null, role: 'school_admin', name: '', username: '' });
  const submitAddUser = async () => {
    if (!addUserDialog.school || !addUserDialog.name.trim() || !addUserDialog.username.trim()) {
      toast.error('Name and username are required');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/super-admin/schools/${addUserDialog.school.id}/add-user`,
        { role: addUserDialog.role, name: addUserDialog.name.trim(), username: addUserDialog.username.trim() },
        { headers: getAuthHeader() }
      );
      toast.success(res.data?.message || 'User created');
      setAddUserDialog({ open: false, school: null, role: 'school_admin', name: '', username: '' });
      setCreatedCredentials({
        schoolName: res.data.school_name,
        headTeacher: res.data.head_teacher || null,
        officeAdmin: res.data.office_admin || null,
      });
      setShowCredentials(true);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    }
  };
  const handleResetSchoolUser = async (school, role) => {
    const label = role === 'school_admin' ? 'Office Admin' : 'Head Teacher';
    if (!window.confirm(`Reset ${label} password for ${school.name}?\n\nThe user will be forced to set a new password on their next login.`)) return;
    try {
      const res = await axios.post(
        `${API}/super-admin/schools/${school.id}/reset-user-password`,
        { role },
        { headers: getAuthHeader() }
      );
      toast.success(res.data?.message || 'Password reset');
      setCreatedCredentials({
        schoolName: res.data.school_name,
        headTeacher: role === 'head_teacher' ? { name: school.head_teacher_name, username: res.data.username, password: res.data.password } : null,
        officeAdmin: role === 'school_admin' ? { name: school.office_admin_name, username: res.data.username, password: res.data.password } : null,
      });
      setShowCredentials(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    }
  };
  const [schoolForm, setSchoolForm] = useState({
    name: '',
    location: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    max_students: 500,
    max_teachers: 50,
    curriculum_board: 'SCERT',
    ht_name: '',
    ht_username: '',
    admin_name: '',
    admin_username: ''
  });
  const [htForm, setHtForm] = useState({
    name: '',
    username: '',
    password: generateDefaultPassword()
  });

  const getAuthHeader = () => {
    const token = localStorage.getItem('superAdminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, schoolsRes, htRes] = await Promise.all([
        axios.get(`${API}/super-admin/dashboard/stats`, { headers: getAuthHeader() }),
        axios.get(`${API}/super-admin/schools`, { headers: getAuthHeader() }),
        axios.get(`${API}/super-admin/head-teachers`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setSchools(schoolsRes.data);
      setHeadTeachers(htRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('superAdminToken');
        navigate('/super-admin/login');
      }
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    localStorage.removeItem('superAdminUser');
    navigate('/');
  };

  const handleAddSchool = async () => {
    try {
      const res = await axios.post(`${API}/super-admin/schools`, schoolForm, { headers: getAuthHeader() });
      toast.success('School created successfully!');
      setShowAddSchool(false);
      setSchoolForm({ name: '', location: '', contact_person: '', contact_email: '', contact_phone: '', max_students: 500, max_teachers: 50, curriculum_board: 'SCERT', ht_name: '', ht_username: '', admin_name: '', admin_username: '' });
      
      // Show combined credentials popup if either account was created
      if (res.data.head_teacher || res.data.office_admin) {
        setCreatedCredentials({
          schoolName: res.data.school_name || schoolForm.name,
          headTeacher: res.data.head_teacher || null,
          officeAdmin: res.data.office_admin || null,
        });
        setShowCredentials(true);
      }
      
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create school');
    }
  };

  const startPreGeneration = async () => {
    try {
      await axios.post(`${API}/super-admin/pre-generate-content`, {}, { headers: getAuthHeader() });
      toast.success('Content pre-generation started!');
      setPreGenPolling(true);
      pollPreGenStatus();
    } catch (error) {
      toast.error('Failed to start pre-generation');
    }
  };

  const stopPreGeneration = async () => {
    try {
      await axios.post(`${API}/super-admin/stop-pre-generate`, {}, { headers: getAuthHeader() });
      toast.info('Pre-generation stopping...');
    } catch (error) {
      toast.error('Failed to stop');
    }
  };

  const pollPreGenStatus = async () => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/super-admin/pre-generate-status`, { headers: getAuthHeader() });
        setPreGenProgress(res.data);
        if (res.data.running) {
          setTimeout(poll, 3000);
        } else {
          setPreGenPolling(false);
          if (res.data.completed_at) {
            toast.success(`Pre-generation complete! ${res.data.completed_images} images, ${res.data.completed_audio} audio files ready.`);
          }
        }
      } catch {
        setPreGenPolling(false);
      }
    };
    poll();
  };



  const handleUpdateSchool = async () => {
    try {
      await axios.put(`${API}/super-admin/schools/${selectedSchool.id}`, schoolForm, { headers: getAuthHeader() });
      toast.success('School updated successfully!');
      setShowEditSchool(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update school');
    }
  };

  const handleDeleteSchool = async (schoolId) => {
    if (!confirm('Are you sure you want to delete this school?')) return;
    try {
      await axios.delete(`${API}/super-admin/schools/${schoolId}`, { headers: getAuthHeader() });
      toast.success('School deleted/deactivated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete school');
    }
  };

  const handleToggleStatus = async (school) => {
    const newStatus = school.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/super-admin/schools/${school.id}/status?status=${newStatus}`, {}, { headers: getAuthHeader() });
      toast.success(`School ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddHeadTeacher = async () => {
    try {
      const res = await axios.post(`${API}/super-admin/head-teachers`, htForm, { headers: getAuthHeader() });
      toast.success(`Head teacher ${htForm.name} created! Username: ${res.data.username}, Password: ${res.data.password}`);
      setShowAddHeadTeacher(false);
      setHtForm({ name: '', username: '', password: generateDefaultPassword() });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create head teacher');
    }
  };

  const handleAssignHeadTeacher = async (schoolId, htId) => {
    try {
      await axios.put(`${API}/super-admin/schools/${schoolId}/assign-head-teacher?head_teacher_id=${htId}`, {}, { headers: getAuthHeader() });
      toast.success('Head teacher assigned!');
      loadData();
    } catch (error) {
      toast.error('Failed to assign head teacher');
    }
  };

  const openEditModal = (school) => {
    setSelectedSchool(school);
    setSchoolForm({
      name: school.name,
      location: school.location,
      contact_person: school.contact_person,
      contact_email: school.contact_email || '',
      contact_phone: school.contact_phone || '',
      max_students: school.max_students,
      max_teachers: school.max_teachers,
      curriculum_board: school.curriculum_board || 'SCERT'
    });
    setShowEditSchool(true);
  };

  const viewSchoolDetails = async (school) => {
    try {
      const res = await axios.get(`${API}/super-admin/schools/${school.id}`, { headers: getAuthHeader() });
      setSelectedSchool(res.data);
      setShowSchoolDetails(true);
    } catch (error) {
      toast.error('Failed to load school details');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-50 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">MyClassAI</h1>
              <p className="text-xs text-slate-400">Super Admin Panel</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => window.open('/docs', '_blank', 'noopener')}
              className="text-slate-300 border-slate-600 hover:bg-slate-700"
              data-testid="open-docs-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Docs
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const chapter = window.prompt('Pre-warm Audio Learning cache for which chapter? (e.g. "Algebra", or leave blank to warm all)', 'Algebra');
                if (chapter === null) return;
                try {
                  const token = localStorage.getItem('superAdminToken');
                  const t = toast.loading('Pre-warming audio cache (this can take ~1 min per 10 topics)…');
                  const qs = chapter.trim() ? `?chapter=${encodeURIComponent(chapter.trim())}&limit=50` : `?limit=50`;
                  const res = await axios.post(`${API}/super-admin/prewarm-audio-cache${qs}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.dismiss(t);
                  const r = res.data;
                  toast.success(`Pre-warm done — fresh: ${r.fresh_generated} · already cached: ${r.already_cached_skipped} · remaining: ${r.remaining_candidates} · errors: ${r.errors}`);
                  if (r.remaining_candidates > 0) {
                    toast.info(`Re-click to warm the remaining ${r.remaining_candidates} topics.`);
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to pre-warm');
                }
              }}
              className="text-cyan-300 border-cyan-700 hover:bg-cyan-900/30"
              data-testid="prewarm-audio-btn"
            >
              <Volume2 className="w-4 h-4 mr-2" />
              Pre-warm Audio
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('superAdminToken');
                  const res = await axios.post(`${API}/super-admin/seed-demo-accounts`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  const c = res.data.created_count || 0;
                  const s = res.data.skipped_count || 0;
                  toast.success(`Seed complete — created ${c}, already existed ${s}. Try teacher1 / password123 and student1 / password123.`);
                  loadData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to seed demo accounts');
                }
              }}
              className="text-emerald-300 border-emerald-700 hover:bg-emerald-900/30"
              data-testid="seed-demo-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Seed Demo Accounts
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm('Permanently delete all auto-generated test accounts (TEST_*, UI Test *, Test Pilot) across all schools? This cannot be undone.')) return;
                try {
                  const token = localStorage.getItem('superAdminToken');
                  const res = await axios.post(`${API}/super-admin/purge-test-data`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(`Purged ${res.data.matched_test_students_by_name} test students and dependent records`);
                  loadData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to purge test data');
                }
              }}
              className="text-red-300 border-red-700 hover:bg-red-900/30"
              data-testid="purge-test-data-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Purge Test Data
            </Button>
            <Button variant="outline" onClick={handleLogout} className="text-slate-300 border-slate-600 hover:bg-slate-700">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Total Schools</p>
                  <p className="text-4xl font-bold mt-1" data-testid="total-schools">{stats?.schools?.total || 0}</p>
                  <p className="text-sm text-amber-100 mt-1">
                    {stats?.schools?.active || 0} active
                  </p>
                </div>
                <Building2 className="w-12 h-12 text-amber-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Students</p>
                  <p className="text-4xl font-bold mt-1" data-testid="total-students">{stats?.users?.students || 0}</p>
                </div>
                <GraduationCap className="w-12 h-12 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Total Teachers</p>
                  <p className="text-4xl font-bold mt-1" data-testid="total-teachers">{stats?.users?.teachers || 0}</p>
                </div>
                <Users className="w-12 h-12 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Test Attempts (7d)</p>
                  <p className="text-4xl font-bold mt-1" data-testid="recent-attempts">{stats?.activity?.recent_attempts_7d || 0}</p>
                </div>
                <Activity className="w-12 h-12 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Usage & Cost Dashboard */}
        <div className="mb-8">
          <AIUsageDashboard />
        </div>

        {/* Pre-generation Card */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Pre-generate Learning Content</h3>
                  <p className="text-slate-400 text-sm">Generate all AI illustrations & audio for instant student access</p>
                </div>
              </div>
              <div className="flex gap-2">
                {preGenProgress?.running && (
                  <Button
                    onClick={stopPreGeneration}
                    variant="outline"
                    className="border-red-500 text-red-400 hover:bg-red-500/10"
                    data-testid="stop-pre-generate-btn"
                  >
                    Stop
                  </Button>
                )}
                <Button
                  onClick={startPreGeneration}
                  disabled={preGenProgress?.running}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="pre-generate-btn"
                >
                  {preGenProgress?.running ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Pre-generate All</>
                  )}
                </Button>
              </div>
            </div>
            {preGenProgress && (preGenProgress.running || preGenProgress.completed_at) && (
              <div className="mt-4 space-y-3">
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-slate-400">Total Topics: <span className="text-white font-medium">{preGenProgress.total_topics}</span></span>
                  <span className="flex items-center gap-1.5 text-slate-400">
                    <ImageIcon className="w-3.5 h-3.5 text-teal-400" /> Images: <span className="text-teal-400 font-medium">{preGenProgress.completed_images}</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-slate-400">
                    <Volume2 className="w-3.5 h-3.5 text-cyan-400" /> Audio: <span className="text-cyan-400 font-medium">{preGenProgress.completed_audio}</span>
                  </span>
                  {preGenProgress.failed > 0 && (
                    <span className="text-red-400">Failed: {preGenProgress.failed}</span>
                  )}
                </div>
                {preGenProgress.running && (
                  <>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all duration-500"
                        style={{ width: `${preGenProgress.total_topics > 0 ? Math.round(((preGenProgress.completed_images + preGenProgress.completed_audio) / (preGenProgress.total_topics * 2)) * 100) : 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500">Currently: {preGenProgress.current_topic}</p>
                  </>
                )}
                {!preGenProgress.running && preGenProgress.completed_at && (
                  <p className="text-xs text-emerald-400">Pre-generation complete! All content is ready for instant access.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schools Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" />
                Schools Management
              </CardTitle>
              <p className="text-slate-400 text-sm mt-1">Manage all registered schools</p>
            </div>
            <div className="flex gap-2">
              {/* Add Head Teacher Button */}
              <Dialog open={showAddHeadTeacher} onOpenChange={setShowAddHeadTeacher}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10" data-testid="add-ht-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Head Teacher
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className="bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto"
                  data-testid="add-ht-dialog"
                >
                  <DialogHeader>
                    <DialogTitle>Create Head Teacher Account</DialogTitle>
                    <DialogDescription className="text-slate-400">
                      Create a new head teacher to assign to a school
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={htForm.name}
                        onChange={(e) => setHtForm({ ...htForm, name: e.target.value })}
                        placeholder="e.g., Dr. Sunita Reddy"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username *</Label>
                      <Input
                        value={htForm.username}
                        onChange={(e) => setHtForm({ ...htForm, username: e.target.value })}
                        placeholder="e.g., principal_reddy"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        value={htForm.password}
                        onChange={(e) => setHtForm({ ...htForm, password: e.target.value })}
                        placeholder="Default: password123"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddHeadTeacher(false)}>Cancel</Button>
                    <Button onClick={handleAddHeadTeacher} className="bg-emerald-500 hover:bg-emerald-600">
                      Create Head Teacher
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              
              {/* Add School Button */}
              <Dialog open={showAddSchool} onOpenChange={setShowAddSchool}>
                <DialogTrigger asChild>
                  <Button className="bg-amber-500 hover:bg-amber-600" data-testid="add-school-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add School
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className="bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto"
                  data-testid="add-school-dialog"
                >
                <DialogHeader>
                  <DialogTitle>Add New School</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Enter school details to register a new institution
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>School Name *</Label>
                    <Input
                      value={schoolForm.name}
                      onChange={(e) => setSchoolForm({ ...schoolForm, name: e.target.value })}
                      placeholder="e.g., Government High School, Hyderabad"
                      className="bg-slate-700 border-slate-600"
                      data-testid="school-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Input
                      value={schoolForm.location}
                      onChange={(e) => setSchoolForm({ ...schoolForm, location: e.target.value })}
                      placeholder="e.g., Hyderabad, Telangana"
                      className="bg-slate-700 border-slate-600"
                      data-testid="school-location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Person *</Label>
                    <Input
                      value={schoolForm.contact_person}
                      onChange={(e) => setSchoolForm({ ...schoolForm, contact_person: e.target.value })}
                      placeholder="e.g., Mr. Ramesh Kumar"
                      className="bg-slate-700 border-slate-600"
                      data-testid="contact-person-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        value={schoolForm.contact_email}
                        onChange={(e) => setSchoolForm({ ...schoolForm, contact_email: e.target.value })}
                        placeholder="school@edu.in"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={schoolForm.contact_phone}
                        onChange={(e) => setSchoolForm({ ...schoolForm, contact_phone: e.target.value })}
                        placeholder="+91 9876543210"
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Max Students</Label>
                      <Input
                        type="number"
                        value={schoolForm.max_students}
                        onChange={(e) => setSchoolForm({ ...schoolForm, max_students: parseInt(e.target.value) })}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Teachers</Label>
                      <Input
                        type="number"
                        value={schoolForm.max_teachers}
                        onChange={(e) => setSchoolForm({ ...schoolForm, max_teachers: parseInt(e.target.value) })}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Curriculum Board</Label>
                    <select
                      value={schoolForm.curriculum_board}
                      onChange={(e) => setSchoolForm({ ...schoolForm, curriculum_board: e.target.value })}
                      className="w-full rounded-md bg-slate-700 border border-slate-600 text-white px-3 py-2"
                      data-testid="curriculum-board-select"
                    >
                      <option value="SCERT">SCERT (State Council)</option>
                      <option value="NCERT">NCERT (National Council)</option>
                    </select>
                  </div>

                  {/* Head Teacher Creation Section */}
                  <div className="border-t border-slate-600 pt-4 mt-4">
                    <p className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Create Head Teacher for this School
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Head Teacher Name *</Label>
                        <Input
                          value={schoolForm.ht_name}
                          onChange={(e) => setSchoolForm({ ...schoolForm, ht_name: e.target.value })}
                          placeholder="e.g., Dr. Sunita Reddy"
                          className="bg-slate-700 border-slate-600"
                          data-testid="ht-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Head Teacher Username *</Label>
                        <Input
                          value={schoolForm.ht_username}
                          onChange={(e) => setSchoolForm({ ...schoolForm, ht_username: e.target.value })}
                          placeholder="e.g., principal_sunita"
                          className="bg-slate-700 border-slate-600"
                          data-testid="ht-username-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Default password: <span className="text-amber-300 font-mono">Welcome@123</span> (Head Teacher will be asked to change it on first login)</p>
                  </div>

                  {/* Office Admin Creation Section */}
                  <div className="border-t border-slate-600 pt-4 mt-4">
                    <p className="text-sm font-semibold text-orange-400 mb-3 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Create Office Admin for this School (Students, Fees, PINs, CSV)
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Office Admin Name</Label>
                        <Input
                          value={schoolForm.admin_name}
                          onChange={(e) => setSchoolForm({ ...schoolForm, admin_name: e.target.value })}
                          placeholder="e.g., Mrs. Priya Rao"
                          className="bg-slate-700 border-slate-600"
                          data-testid="admin-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Office Admin Username</Label>
                        <Input
                          value={schoolForm.admin_username}
                          onChange={(e) => setSchoolForm({ ...schoolForm, admin_username: e.target.value })}
                          placeholder="e.g., admin_priya"
                          className="bg-slate-700 border-slate-600"
                          data-testid="admin-username-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Default password: <span className="text-orange-300 font-mono">Welcome@Admin123</span>. Leave blank to skip creating an office admin.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddSchool(false)} className="border-slate-600">
                    Cancel
                  </Button>
                  <Button onClick={handleAddSchool} className="bg-amber-500 hover:bg-amber-600" data-testid="save-school-btn">
                    Create School
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {schools.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No schools registered yet</p>
                <p className="text-slate-500 text-sm">Click "Add School" to register your first school</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="schools-table">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">School Name</th>
                      <th className="text-left py-3 px-4 text-slate-400 font-medium">Location</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Board</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Students</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Teachers</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Status</th>
                      <th className="text-center py-3 px-4 text-slate-400 font-medium">Head Teacher</th>
                      <th className="text-right py-3 px-4 text-slate-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((school) => (
                      <tr key={school.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                              <School className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                              <p className="text-white font-medium" data-testid={`school-name-${school.id}`}>{school.name}</p>
                              <p className="text-slate-500 text-xs">{school.contact_person}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-slate-300">{school.location}</td>
                        <td className="py-4 px-4 text-center">
                          <Badge 
                            className={school.curriculum_board === 'NCERT' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}
                            data-testid={`school-board-${school.id}`}
                          >
                            {school.curriculum_board || 'SCERT'}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="text-white font-semibold">{school.student_count}</span>
                          <span className="text-slate-500 text-xs">/{school.max_students}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="text-white font-semibold">{school.teacher_count}</span>
                          <span className="text-slate-500 text-xs">/{school.max_teachers}</span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={school.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                            {school.status}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          {school.head_teacher_name ? (
                            <span className="text-slate-300">{school.head_teacher_name}</span>
                          ) : (
                            <Select onValueChange={(value) => handleAssignHeadTeacher(school.id, value)}>
                              <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-xs">
                                <SelectValue placeholder="Assign" />
                              </SelectTrigger>
                              <SelectContent>
                                {headTeachers.map(ht => (
                                  <SelectItem key={ht.id} value={ht.id}>
                                    {ht.name} {ht.assigned_school ? `(${ht.assigned_school})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex justify-end gap-2">
                            {(!school.head_teacher_name || !school.office_admin_name) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setAddUserDialog({
                                  open: true,
                                  school,
                                  role: !school.office_admin_name ? 'school_admin' : 'head_teacher',
                                  name: '',
                                  username: '',
                                })}
                                className="text-amber-400 hover:text-amber-300"
                                title={!school.office_admin_name ? 'Add Office Admin' : 'Add Head Teacher'}
                                data-testid={`add-user-btn-${school.id}`}
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => viewSchoolDetails(school)} className="text-slate-400 hover:text-white">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditModal(school)} className="text-slate-400 hover:text-white">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleToggleStatus(school)} className="text-slate-400 hover:text-white">
                              {school.status === 'active' ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteSchool(school.id)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit School Modal */}
        <Dialog open={showEditSchool} onOpenChange={setShowEditSchool}>
          <DialogContent
            className="bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto"
            data-testid="edit-school-dialog"
          >
            <DialogHeader>
              <DialogTitle>Edit School</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>School Name</Label>
                <Input
                  value={schoolForm.name}
                  onChange={(e) => setSchoolForm({ ...schoolForm, name: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={schoolForm.location}
                  onChange={(e) => setSchoolForm({ ...schoolForm, location: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  value={schoolForm.contact_person}
                  onChange={(e) => setSchoolForm({ ...schoolForm, contact_person: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Students</Label>
                  <Input
                    type="number"
                    value={schoolForm.max_students}
                    onChange={(e) => setSchoolForm({ ...schoolForm, max_students: parseInt(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Teachers</Label>
                  <Input
                    type="number"
                    value={schoolForm.max_teachers}
                    onChange={(e) => setSchoolForm({ ...schoolForm, max_teachers: parseInt(e.target.value) })}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Curriculum Board</Label>
                <select
                  value={schoolForm.curriculum_board || 'SCERT'}
                  onChange={(e) => setSchoolForm({ ...schoolForm, curriculum_board: e.target.value })}
                  className="w-full rounded-md bg-slate-700 border border-slate-600 text-white px-3 py-2"
                  data-testid="edit-curriculum-board-select"
                >
                  <option value="SCERT">SCERT (State Council)</option>
                  <option value="NCERT">NCERT (National Council)</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditSchool(false)} className="border-slate-600">Cancel</Button>
              <Button onClick={handleUpdateSchool} className="bg-amber-500 hover:bg-amber-600">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* School Details Modal */}
        <Dialog open={showSchoolDetails} onOpenChange={setShowSchoolDetails}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <School className="w-5 h-5 text-amber-500" />
                {selectedSchool?.name}
              </DialogTitle>
            </DialogHeader>
            {selectedSchool && (
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Location</p>
                    <p className="text-white font-medium">{selectedSchool.location}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Contact Person</p>
                    <p className="text-white font-medium">{selectedSchool.contact_person}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Curriculum Board</p>
                    <Badge className={selectedSchool.curriculum_board === 'NCERT' ? 'bg-blue-500/20 text-blue-400 mt-1' : 'bg-amber-500/20 text-amber-400 mt-1'}>
                      {selectedSchool.curriculum_board || 'SCERT'}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-500/20 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">{selectedSchool.statistics?.student_count || 0}</p>
                    <p className="text-slate-400 text-sm">Students</p>
                  </div>
                  <div className="bg-emerald-500/20 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-400">{selectedSchool.statistics?.teacher_count || 0}</p>
                    <p className="text-slate-400 text-sm">Teachers</p>
                  </div>
                  <div className="bg-purple-500/20 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-purple-400">{selectedSchool.statistics?.test_count || 0}</p>
                    <p className="text-slate-400 text-sm">Tests</p>
                  </div>
                </div>

                {/* School Users — HT + Office Admin quick-add */}
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-3">School Users</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-amber-500/30">
                      <p className="text-[10px] uppercase tracking-wide text-amber-300 mb-1">Head Teacher</p>
                      {selectedSchool.head_teacher_name ? (
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-white font-medium text-sm">{selectedSchool.head_teacher_name}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-300 border-amber-500/40 hover:bg-amber-500/10 h-6 text-xs px-2"
                            data-testid={`reset-ht-${selectedSchool.id}`}
                            onClick={() => handleResetSchoolUser(selectedSchool, 'head_teacher')}
                          >
                            Reset PW
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setAddUserDialog({ open: true, school: selectedSchool, role: 'head_teacher', name: '', username: '' })}
                          className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-7 mt-1"
                          data-testid={`details-add-ht-${selectedSchool.id}`}
                        >
                          <UserPlus className="w-3 h-3 mr-1" /> Add Head Teacher
                        </Button>
                      )}
                    </div>
                    <div className="bg-slate-800/60 rounded-lg p-3 border border-orange-500/30">
                      <p className="text-[10px] uppercase tracking-wide text-orange-300 mb-1">Office Admin</p>
                      {selectedSchool.office_admin_name ? (
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-white font-medium text-sm">
                            {selectedSchool.office_admin_name}
                            <span className="text-slate-500 text-xs ml-1">({selectedSchool.office_admin_username})</span>
                          </p>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-300 border-orange-500/40 hover:bg-orange-500/10 h-6 text-xs px-2"
                            data-testid={`reset-admin-${selectedSchool.id}`}
                            onClick={() => handleResetSchoolUser(selectedSchool, 'school_admin')}
                          >
                            Reset PW
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setAddUserDialog({ open: true, school: selectedSchool, role: 'school_admin', name: '', username: '' })}
                          className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-7 mt-1"
                          data-testid={`details-add-admin-${selectedSchool.id}`}
                        >
                          <UserPlus className="w-3 h-3 mr-1" /> Add Office Admin
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {selectedSchool.statistics?.class_breakdown && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-3">Class Distribution</p>
                    <div className="flex gap-4">
                      <div className="flex-1 text-center">
                        <p className="text-xl font-bold text-white">{selectedSchool.statistics.class_breakdown.class_6 || 0}</p>
                        <p className="text-slate-500 text-xs">Class 6</p>
                      </div>
                      <div className="flex-1 text-center">
                        <p className="text-xl font-bold text-white">{selectedSchool.statistics.class_breakdown.class_7 || 0}</p>
                        <p className="text-slate-500 text-xs">Class 7</p>
                      </div>
                      <div className="flex-1 text-center">
                        <p className="text-xl font-bold text-white">{selectedSchool.statistics.class_breakdown.class_8 || 0}</p>
                        <p className="text-slate-500 text-xs">Class 8</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedSchool.head_teacher && (
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-2">Head Teacher</p>
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-8 h-8 text-amber-500" />
                      <div>
                        <p className="text-white font-medium">{selectedSchool.head_teacher.name}</p>
                        <p className="text-slate-500 text-sm">@{selectedSchool.head_teacher.username}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add User to Existing School Dialog */}
        <Dialog
          open={addUserDialog.open}
          onOpenChange={(open) => setAddUserDialog({ ...addUserDialog, open })}
        >
          <DialogContent
            className="bg-slate-800 border-slate-700 text-white max-h-[90vh] overflow-y-auto"
            data-testid="add-user-dialog"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-amber-400" />
                Add {addUserDialog.role === 'school_admin' ? 'Office Admin' : 'Head Teacher'}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                For school <span className="text-white font-medium">{addUserDialog.school?.name}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Role *</Label>
                <select
                  className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white"
                  value={addUserDialog.role}
                  onChange={(e) => setAddUserDialog({ ...addUserDialog, role: e.target.value })}
                  data-testid="add-user-role-select"
                >
                  {!addUserDialog.school?.office_admin_name && (
                    <option value="school_admin">Office Admin (Students · Fees · PIN · CSV)</option>
                  )}
                  {!addUserDialog.school?.head_teacher_name && (
                    <option value="head_teacher">Head Teacher / Principal (Academic Oversight)</option>
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={addUserDialog.name}
                  onChange={(e) => setAddUserDialog({ ...addUserDialog, name: e.target.value })}
                  placeholder={addUserDialog.role === 'school_admin' ? 'e.g., Mrs. Priya Rao' : 'e.g., Dr. Sunita Reddy'}
                  className="bg-slate-700 border-slate-600"
                  data-testid="add-user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Username *</Label>
                <Input
                  value={addUserDialog.username}
                  onChange={(e) => setAddUserDialog({ ...addUserDialog, username: e.target.value })}
                  placeholder={addUserDialog.role === 'school_admin' ? 'e.g., admin_priya' : 'e.g., principal_sunita'}
                  className="bg-slate-700 border-slate-600"
                  data-testid="add-user-username-input"
                />
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300">
                Default password: <span className="font-mono text-amber-200">
                  {addUserDialog.role === 'school_admin' ? 'Welcome@Admin123' : 'Welcome@123'}
                </span>. The user must change it on first login.
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setAddUserDialog({ ...addUserDialog, open: false })}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button
                onClick={submitAddUser}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="add-user-submit-btn"
              >
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Combined Onboarding Pack Popup */}
        <Dialog open={showCredentials} onOpenChange={setShowCredentials}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-400">
                <UserCheck className="w-5 h-5" />
                School Onboarding Pack
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Share these login credentials with the staff at <span className="text-white font-medium">{createdCredentials?.schoolName}</span>. Both roles must change their password on first login.
              </DialogDescription>
            </DialogHeader>
            {createdCredentials && (
              <div className="space-y-4 py-2" data-testid="onboarding-pack">
                {createdCredentials.headTeacher && (
                  <div className="bg-slate-700/60 rounded-lg p-4 border border-amber-500/40" data-testid="ht-cred-block">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-amber-300">Head Teacher / Principal</p>
                      <span className="text-[10px] uppercase font-semibold text-amber-200/70">/head-teacher/login</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm items-center">
                      <span className="text-slate-400">Name</span>
                      <span className="col-span-2 text-white font-medium" data-testid="cred-name">{createdCredentials.headTeacher.name}</span>
                      <span className="text-slate-400">Username</span>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-amber-400 font-mono font-bold" data-testid="cred-username">{createdCredentials.headTeacher.username}</span>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(createdCredentials.headTeacher.username); toast.success('Username copied'); }}
                          className="text-slate-400 hover:text-amber-300 text-xs underline"
                          data-testid="copy-ht-username-btn"
                        >
                          Copy
                        </button>
                      </div>
                      <span className="text-slate-400">Password</span>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-amber-400 font-mono font-bold" data-testid="cred-password">{createdCredentials.headTeacher.password}</span>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(createdCredentials.headTeacher.password); toast.success('Password copied — paste directly, no need to retype'); }}
                          className="text-slate-400 hover:text-amber-300 text-xs underline"
                          data-testid="copy-ht-password-btn"
                        >
                          Copy
                        </button>
                      </div>
                      <span className="text-slate-400">Login URL</span>
                      <span className="col-span-2 text-blue-300 font-mono text-xs break-all">{window.location.origin}/head-teacher/login</span>
                    </div>
                  </div>
                )}
                {createdCredentials.officeAdmin && (
                  <div className="bg-slate-700/60 rounded-lg p-4 border border-orange-500/40" data-testid="admin-cred-block">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold uppercase tracking-wide text-orange-300">Office Admin (Ops)</p>
                      <span className="text-[10px] uppercase font-semibold text-orange-200/70">/admin/login</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm items-center">
                      <span className="text-slate-400">Name</span>
                      <span className="col-span-2 text-white font-medium">{createdCredentials.officeAdmin.name}</span>
                      <span className="text-slate-400">Username</span>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-orange-400 font-mono font-bold">{createdCredentials.officeAdmin.username}</span>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(createdCredentials.officeAdmin.username); toast.success('Username copied'); }}
                          className="text-slate-400 hover:text-orange-300 text-xs underline"
                          data-testid="copy-admin-username-btn"
                        >
                          Copy
                        </button>
                      </div>
                      <span className="text-slate-400">Password</span>
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-orange-400 font-mono font-bold">{createdCredentials.officeAdmin.password}</span>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(createdCredentials.officeAdmin.password); toast.success('Password copied — paste directly, no need to retype'); }}
                          className="text-slate-400 hover:text-orange-300 text-xs underline"
                          data-testid="copy-admin-password-btn"
                        >
                          Copy
                        </button>
                      </div>
                      <span className="text-slate-400">Login URL</span>
                      <span className="col-span-2 text-blue-300 font-mono text-xs break-all">{window.location.origin}/admin/login</span>
                    </div>
                  </div>
                )}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-amber-300 text-xs text-center">
                    Both users will be prompted to change their password on first login.
                  </p>
                </div>
                <Button 
                  onClick={() => {
                    const ht = createdCredentials.headTeacher;
                    const ad = createdCredentials.officeAdmin;
                    const parts = [`MyClassAI — Onboarding Pack`, `School: ${createdCredentials.schoolName}`, ''];
                    if (ht) parts.push(
                      `HEAD TEACHER / PRINCIPAL`,
                      `Login URL: ${window.location.origin}/head-teacher/login`,
                      `Name: ${ht.name}`,
                      `Username: ${ht.username}`,
                      `Password: ${ht.password}`,
                      ''
                    );
                    if (ad) parts.push(
                      `OFFICE ADMIN (Students · Fees · PINs · CSV)`,
                      `Login URL: ${window.location.origin}/admin/login`,
                      `Name: ${ad.name}`,
                      `Username: ${ad.username}`,
                      `Password: ${ad.password}`,
                      ''
                    );
                    parts.push('Both users will be asked to change their password on first login.');
                    navigator.clipboard.writeText(parts.join('\n'));
                    toast.success('Onboarding pack copied to clipboard!');
                  }}
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  data-testid="copy-credentials-btn"
                >
                  Copy Full Onboarding Pack to Clipboard
                </Button>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setShowCredentials(false)} className="bg-slate-600 hover:bg-slate-500">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
